import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dumbbell, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import LoadFailureNote from "./LoadFailureNote";
import { createDrill, fetchMyDrills, updateDrill } from "../lib/drills";
import { coachPlayersName } from "../lib/coachPlayersName";

/**
 * Issue #509 — the drill BANK: where a coach's drills are authored.
 *
 * #498 put drill authoring inside Coach Portal → player → Guides tab, which
 * tangled creation with assignment to one player. Andreas's direction
 * (2026-08-27): authoring lives on the coach's Training surface as a bank —
 * a drill is account-scoped (`coach_drills` carries no player), so no
 * player context is needed to write one — and the per-player surface keeps
 * ASSIGNMENT only (`CoachDrillsPanel.jsx`, now assign-only).
 *
 * MOUNTED on LearningHub's Training tab (`/learn?tab=training`), which is
 * the coach-side Training home: COACH_NAV's "Training" entry points there
 * (Layout.jsx, #101) and it is where a coach already schedules sessions
 * (`TrainingSessions`). Gated to coach|admin by the caller — the same two
 * roles the API's write gate admits (`api/src/routes/drills.ts`,
 * `isCoachOrAdmin`). A player never sees this; their drill surface is the
 * Assigned tab.
 *
 * DRILLS ONLY, and the copy says so: reviewed Quick Reference guides are
 * the #98 pipeline (import → named sign-off → publish) and are NOT
 * authored here or anywhere else by a coach — see #509 point 5. A drill
 * needs no review gate because it is the coach's OWN words to their own
 * players, the same trust footing as feedback notes (the drills route's
 * header).
 *
 * THE CATALOGUE FETCH IS NOT SWALLOWED (#511's second bug, the #271
 * class): a failed `GET /drills` renders LoadFailureNote + retry, and the
 * "no drills yet" line renders only once the list actually arrived — never
 * a claim of empty about a list this panel failed to fetch.
 *
 * Bounds mirror the API's (title ≤ 120, body ≤ 4000 — Zod and the table's
 * CHECKs state the same numbers); `maxLength` here is a courtesy, the
 * server is the gate.
 */
const TITLE_MAX = 120;
const BODY_MAX = 4000;
const TOUCH = "min-h-[44px]";

export default function CoachDrillBankPanel() {
  const [drills, setDrills] = useState([]);
  // "loading" | "loaded" | "failed" — three states, because "empty" may
  // only be claimed from "loaded" (#271/#511).
  const [bankStatus, setBankStatus] = useState("loading");
  const [retryNonce, setRetryNonce] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  // { drillId, title, body } while an inline edit is open, null otherwise.
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setBankStatus("loading");
    fetchMyDrills()
      .then((rows) => {
        if (cancelled) return;
        setDrills(rows);
        setBankStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setBankStatus("failed");
      });
    return () => { cancelled = true; };
  }, [retryNonce]);

  const create = async () => {
    if (!title.trim() || !body.trim() || busy) return;
    setBusy(true);
    try {
      const drill = await createDrill(title.trim(), body.trim());
      // Newest first, matching the server's own order (listDrillsForCoach).
      setDrills((prev) => [drill, ...prev]);
      setTitle("");
      setBody("");
      toast.success("Drill added to your bank");
    } catch {
      toast.error("Could not save that drill. Check the title and text and try again.");
    }
    setBusy(false);
  };

  const saveEdit = async () => {
    if (!editing || !editing.title.trim() || !editing.body.trim()) return;
    try {
      const updated = await updateDrill(editing.drillId, editing.title.trim(), editing.body.trim());
      setDrills((prev) => prev.map((d) => (d.drill_id === updated.drill_id ? updated : d)));
      setEditing(null);
      // The assignment is a pointer (repos/coachDrills.ts), so every player
      // this drill is assigned to sees the edit on their next read.
      toast.success("Drill updated — assigned players see the new version");
    } catch {
      toast.error("Could not save the edit. Check the title and text and try again.");
    }
  };

  return (
    <Card className="border-slate-200" data-testid="drill-bank-panel">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0B2E5915" }}>
            <Dumbbell size={18} color="#0B2E59" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-bold text-lg text-slate-900">Drill bank</h3>
            <p className="text-sm text-slate-500 mt-0.5 break-words">
              Write a drill once, in your own words, and assign it to any of your players from their page in{" "}
              <Link to="/coach-portal" className="underline text-[#0B2E59] font-medium">{coachPlayersName()}</Link>. Drills
              only: reviewed guides aren't written here — they reach players through the review pipeline.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={TITLE_MAX}
            placeholder="Drill title — e.g. Front-foot drive shadow reps"
            className={TOUCH}
            data-testid="drill-bank-title"
          />
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={BODY_MAX}
            rows={4}
            placeholder="The drill itself: setup, reps, what to focus on."
            data-testid="drill-bank-body"
          />
          <Button
            onClick={create}
            disabled={!title.trim() || !body.trim() || busy}
            className={TOUCH}
            style={{ background: "#0B2E59" }}
            data-testid="drill-bank-create"
          >
            <Plus size={14} className="mr-1.5" /> {busy ? "Saving…" : "Create a drill"}
          </Button>
        </div>

        <div className="space-y-2" data-testid="drill-bank-list">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Your drills</div>

          {bankStatus === "failed" && (
            <LoadFailureNote
              what="your drills"
              testId="drill-bank-error"
              onRetry={() => setRetryNonce((n) => n + 1)}
            />
          )}

          {/* "Empty" is a claim about the bank, so it may only render once
              the bank has actually come back (#284's rosterLoaded shape). */}
          {bankStatus === "loaded" && drills.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="drill-bank-empty">
              No drills yet. Write one above — it stays in this bank, ready to assign to any of your players.
            </p>
          )}

          {drills.map((d) => {
            const isEditing = editing?.drillId === d.drill_id;
            return (
              <div key={d.drill_id} className="rounded-xl border border-slate-200 p-3" data-testid={`drill-bank-drill-${d.drill_id}`}>
                {isEditing ? (
                  <div className="space-y-2">
                    <Input
                      value={editing.title}
                      onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                      maxLength={TITLE_MAX}
                      className={TOUCH}
                      data-testid="drill-bank-edit-title"
                    />
                    <Textarea
                      value={editing.body}
                      onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                      maxLength={BODY_MAX}
                      rows={3}
                      data-testid="drill-bank-edit-body"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={saveEdit}
                        disabled={!editing.title.trim() || !editing.body.trim()}
                        style={{ background: "#0B2E59" }}
                        data-testid="drill-bank-edit-save"
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)} data-testid="drill-bank-edit-cancel">
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-slate-900">{d.title}</div>
                      <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{d.body}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="shrink-0"
                      onClick={() => setEditing({ drillId: d.drill_id, title: d.title, body: d.body })}
                      aria-label={`Edit ${d.title}`}
                      data-testid={`drill-bank-edit-${d.drill_id}`}
                    >
                      <Pencil size={14} className="text-slate-400" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
